import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { useThreekit } from '../../threekit-context';
import { useModal } from '../../modal-context';
import Image from '../../../utils/image';
import ThreekitIcons from '../threekit-icons';
import parseCartItems from '../../../utils/parse-cart-items';
import CartItemCustomSelections from '../../../cart/cart-item-custom-selections';
import { formatMoney } from '../../../utils/helpers';
import ErrorModal from './error-modal';

export const socialShareModalName = 'social-share';

function SocialShareModal() {
  const [socialKnifeData, setSocialKnifeData] = useState();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  const { modalName, closeModal } = useModal();
  const {
    getKnifeDataForCart, getAllVariants, products, productHandle,
  } = useThreekit();

  const allVariants = getAllVariants();
  const knifePrice = allVariants.subtotal;

  const isOpen = modalName === socialShareModalName;

  const getKnifeData = useCallback(async () => {
    const knifeData = await getKnifeDataForCart(false, false);

    if (!knifeData) {
      closeModal();
      setError(true);
      setTimeout(() => setError(false), 10000);
      return;
    }

    const parsedKnife = parseCartItems(knifeData?.items || [])[0];
    const imageSrc = parsedKnife.attributes.find(
      (attr) => attr.key === '_product_image_url',
    )?.value;

    const knifeShopifyProduct = products.find((product) =>
      product.variants.edges.find(
        (variant) => variant.node.id === parsedKnife.id,
      ),
    );

    setSocialKnifeData({
      ...knifeData,
      imageSrc,
      parsedKnife,
      knifeShopifyProduct,
    });
  }, [getKnifeDataForCart, products]);

  useEffect(() => {
    if (isOpen && !socialKnifeData) {
      getKnifeData();
    } else if (!isOpen && socialKnifeData) {
      setSocialKnifeData();
    }
  }, [getKnifeData, isOpen, socialKnifeData]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      [
        window.location.protocol,
        '//',
        window.location.host,
        `/products/${productHandle}`,
        '?tkcsid=',
        socialKnifeData.id,
        '&customizerOpen=true',
      ].join(''),
    );

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  if (error) {
    return (
      <ErrorModal>{window.TMW.Strings.customizerError.errorMessage}</ErrorModal>
    );
  }

  return isOpen && socialKnifeData ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center w-full h-full"
      data-share-modal
    >
      <div className="bg-white w-[335px] laptop:w-[440px] rounded px-[20px] py-[20px] laptop:py-[27px]">
        <div className="flex flex-col h-full">
          <div className="flex justify-between pb-[20px] pr-6 border-b border-grey-300 laptop:pb-[27px] laptop:px-0">
            <h4 className="heading-5">{window.TMW.Strings.shareKnife.title}</h4>
            <button type="button" onClick={closeModal}>
              <ThreekitIcons type="exitIcon" />
            </button>
          </div>
          <div>
            <div className="grid grid-cols-12 mt-[40px] border-b border-grey-400 last-of-type:border-0 grow items-center">
              <div className="col-span-5">
                {socialKnifeData.imageSrc && (
                  <Image
                    src={socialKnifeData.imageSrc}
                    className="block object-contain laptop:max-w-[135px] max-w-[98px]"
                  />
                )}
              </div>
              <div className="col-span-7 laptop:pl-4">
                <div>
                  <p className="uppercase text-accent-1 font-r-12 mb-[2px]">
                    CSTM
                  </p>
                  <p className="block mb-1 heading-6">
                    {socialKnifeData.knifeShopifyProduct.title}
                  </p>
                  <p className="font-r-12 mb-[11px]">
                    {formatMoney({ amount: knifePrice })}
                  </p>
                  <CartItemCustomSelections
                    configuration={
                      socialKnifeData.parsedKnife.knifeConfiguration
                    }
                    showMoreSelections={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleCopyLink}
            className="mx-auto flex justify-between items-center my-[20px] font-m-12 uppercase w-[300px] px-[20px] py-[17px] border-[0.6px] rounded-sm border-black text-black hover:border-accent-1"
          >
            <span>
              {copied
                ? window.TMW.Strings.shareKnife.copySuccess
                : window.TMW.Strings.shareKnife.copyLink}
            </span>
            <ThreekitIcons type="link" />
          </button>
        </div>
      </div>
    </div>
  ) : null;
}

export default SocialShareModal;
